/**
 * ManifestTreeFlow — a STANDALONE, left-to-right React Flow view for GENERATIVE
 * (manifest) org-trees. Deliberately SEPARATE from GraphVisualization (which is tuned
 * for the ASL top-down DAG): a manifest is a pure tree that fans WIDE, so it needs its
 * own layout with generous spacing and horizontal ranks.
 *
 * Intentionally PLAIN React Flow — default node rendering, no custom node types, no
 * portals, no highlight/active plumbing. The one job is: take a ``{nodes, edges}`` graph
 * (as produced by ``manifestToReactFlow``) and lay it out cleanly LEFT → RIGHT so a
 * multi-layer org spreads across the canvas instead of piling into a corner.
 *
 * It does NOT touch or reuse the ASL renderer, so it cannot break it.
 */
import React, { useMemo, memo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';

/** Does this manifest node correspond to the workbench-selected agent (its logicalId)?
 *  Node ids are PATH form (``root/Lead/Specialist``) and ``data.label`` is the logicalId,
 *  so match on the label first, then the id's last segment — mirrors the ASL renderer's
 *  ``nodeMatchesWorkbenchHighlight`` so selection highlights consistently across both. */
function matchesSelected(node, highlightLogicalId) {
  if (!highlightLogicalId) return false;
  const h = String(highlightLogicalId);
  const label = String(node?.data?.label ?? '');
  if (label && label === h) return true;
  const id = String(node?.id ?? '');
  return id === h || id.endsWith(`/${h}`) || id.endsWith(`__${h}`);
}

// Generous, tree-friendly sizing so nodes are readable and never overlap.
const NODE_W = 200;
const NODE_H = 52;
// Big separations = the "spread it out" the org-tree needs. ranksep is the horizontal
// gap between LAYERS (root → leads → specialists); nodesep is the gap between SIBLINGS.
const RANKSEP = 140;   // between depth layers (horizontal, since LR)
const NODESEP = 40;    // between siblings in the same layer (vertical, since LR)

/** Kind → a distinct card style (worker vs builder vs start/stop terminals). */
function nodeStyleFor(aslType) {
  if (aslType === 'start') {
    return { background: '#065f46', color: '#fff', border: '1px solid #047857' };
  }
  if (aslType === 'end') {
    return { background: '#7f1d1d', color: '#fff', border: '1px solid #991b1b' };
  }
  if (aslType === 'builder') {
    return { background: '#1e3a8a', color: '#fff', border: '1px solid #2563eb' };
  }
  // worker (default)
  return { background: '#ffffff', color: '#111827', border: '1px solid #6366f1' };
}

// LIVE run-status → node tint. Overlays the base kind-style so the docked room tree reflects the
// AppSync stream in real time: running (blue, pulsing via Tailwind ``animate-pulse``), awaiting
// (amber, pulsing), done (emerald), failed (rose), skipped/not-selected (faded dashed gray), queued
// (default kind-style, untinted). Returns { style, className } to merge onto the node.
function liveStatusStyleFor(status, notSelected) {
  if (notSelected) {
    return { style: { background: '#f8fafc', color: '#94a3b8', border: '1px dashed #cbd5e1', opacity: 0.55 }, className: '' };
  }
  switch (status) {
    case 'running':
      return { style: { background: '#eff6ff', color: '#1e3a8a', border: '2px solid #3b82f6' }, className: 'animate-pulse' };
    case 'awaiting':
      return { style: { background: '#fffbeb', color: '#92400e', border: '2px solid #f59e0b' }, className: 'animate-pulse' };
    case 'done':
      return { style: { background: '#ecfdf5', color: '#065f46', border: '2px solid #10b981' }, className: '' };
    case 'failed':
      return { style: { background: '#fef2f2', color: '#991b1b', border: '2px solid #ef4444' }, className: '' };
    default:
      return null;   // queued / unknown → keep the base kind-style
  }
}

/**
 * ManifestNode — custom tree node. Renders the agent name + the architect's advisory
 * subtitles (capability / model recommendation) inline, and — restoring the behavior the
 * old ASL diagram had — shows the agent's ROLE (``data.comment``, from the manifest's
 * ``description``/``job``) as a rich card on HOVER. Uses the same portal pattern as the ASL
 * ``AgentNode`` so the tooltip layers above the canvas and never clips inside overflow.
 * Terminals (start/stop) have no role → plain label, no hover.
 */
const ManifestNode = memo(({ data }) => {
  const aslType = data?.aslType;
  const isAgent = aslType === 'builder' || aslType === 'worker';
  const name = data?.label ?? '';
  const role = isAgent ? String(data?.comment || '').trim() : '';
  const hasRole = role.length > 0;
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0 });

  const onEnter = (e) => {
    if (!hasRole) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTooltip({ show: true, x: r.left + r.width / 2, y: r.top });
  };
  const onLeave = () => setTooltip((t) => ({ ...t, show: false }));

  return (
    <>
      <div
        className="flex h-full w-full flex-col items-center justify-center overflow-hidden text-center"
        style={{ lineHeight: 1.2, cursor: hasRole ? 'help' : 'default' }}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <Handle type="target" position={Position.Left} className="!bg-gray-400" />
        <span className="w-full truncate px-1 font-medium">{name}</span>
        <Handle type="source" position={Position.Right} className="!bg-gray-400" />
      </div>
      {hasRole && tooltip.show && createPortal(
        <div
          className="fixed z-[99999] pointer-events-none rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-normal text-gray-800 shadow-xl"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, calc(-100% - 8px))',
            whiteSpace: 'pre-wrap',
            minWidth: 320,
            maxWidth: 480,
          }}
        >
          {role}
        </div>,
        document.body,
      )}
    </>
  );
});
ManifestNode.displayName = 'ManifestNode';

const nodeTypes = { manifestNode: ManifestNode };

/** logicalId for a node (label, else last path segment) — the key the status map is keyed by. */
function nodeAgentId(node) {
  const label = String(node?.data?.label ?? '').trim();
  if (label) return label;
  const id = String(node?.id ?? '');
  const seg = id.split('/').pop();
  return seg || id;
}

// Static (kind-independent) node box style — dims + layout. Live tint / kind color / highlight ring
// merge ON TOP of this in ``decorateNode``.
const STATIC_NODE_STYLE = {
  width: NODE_W,
  height: NODE_H,
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 6,
};
const HIGHLIGHT_RING = {
  border: '2px solid #f59e0b',
  boxShadow: '0 0 0 3px #fef08a, 0 4px 14px rgba(234, 88, 12, 0.35)',
};

// CAPPED-OUT (manual stage cap): an agent that exists in the org but is DISABLED this release
// because the user limited run depth (stageDepthCap). Dimmed + dashed so it reads as "part of the
// team, not engaged now" — visually distinct enough from a live "skipped" tint (this is a
// deliberate config state, not a per-run routing decision). Applied UNDER highlight so a user can
// still click + inspect it.
const CAPPED_OUT_STYLE = {
  background: '#f8fafc',
  // Darker text than the box tint so the disabled agent's NAME stays readable — the grey
  // background + dashed border already signal "off"; the label shouldn't fade to invisible.
  color: '#64748b',
  border: '1px dashed #cbd5e1',
  opacity: 0.85,
};

/**
 * Overlay live run-status + workbench selection onto ONE positioned node — recomputing its
 * ``style``/``className``/``data.highlighted`` from (kind, live status, highlight). Pure + cheap:
 * this runs on every status tick WITHOUT re-laying-out the graph, so ReactFlow reconciles each
 * node in place (positions — incl. user drags — are preserved). ``statusByAgentId`` absent
 * (workbench) → only the base kind-style + highlight apply.
 */
function decorateNode(node, highlightLogicalId, statusByAgentId, dimmedAgentIds) {
  const aslType = node.data?.aslType;
  const isAgentNode = aslType === 'builder' || aslType === 'worker';
  const highlighted = matchesSelected(node, highlightLogicalId);
  // Capped out by the manual stage cap (workbench: live draft cap; room: frozen release cap).
  const cappedOut = !!(dimmedAgentIds && isAgentNode && dimmedAgentIds.has(nodeAgentId(node)));
  // A capped agent never runs, so ignore any live status tint on it — CAPPED_OUT_STYLE owns its
  // look. This is the room-side fix: without it, once a run settled the roster marked the capped
  // node ``notSelected`` and ``liveStatusStyleFor`` returned its faded (opacity 0.55) style, which
  // — spread AFTER CAPPED_OUT_STYLE — re-faded the node and made the label near-invisible again.
  const liveEntry = (statusByAgentId && isAgentNode && !cappedOut) ? statusByAgentId[nodeAgentId(node)] : null;
  const live = liveEntry ? liveStatusStyleFor(liveEntry.status, liveEntry.notSelected) : null;
  return {
    ...node,
    // The custom ``manifestNode`` reads ``data.highlighted``; keep the rest of the data slot.
    data: { ...(node.data || {}), highlighted },
    className: live?.className || undefined,
    style: {
      ...STATIC_NODE_STYLE,
      ...nodeStyleFor(aslType),
      // LIVE status overlay wins over the base kind-style (running/done/skipped/…) for
      // NON-capped nodes.
      ...(live?.style || {}),
      // Capped-out dim is the strongest signal (a disabled stage reads disabled regardless of any
      // stale status) — applied AFTER live so it can't be re-faded by a settled ``notSelected`` tint.
      ...(cappedOut ? CAPPED_OUT_STYLE : {}),
      // Selection ring layers on top of everything so an inspected node is always framed.
      ...(highlighted ? HIGHLIGHT_RING : {}),
    },
  };
}

/**
 * Lay the graph out LEFT→RIGHT with dagre → positioned nodes/edges. PURE TOPOLOGY ONLY (no live
 * status, no highlight) so it's memoized on ``data`` alone and dagre runs ONCE per topology — not
 * on every status tick. Live tint + selection are applied afterwards by ``decorateNode`` in place.
 */
function layoutTopology(graph) {
  const inNodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
  const inEdges = Array.isArray(graph?.edges) ? graph.edges : [];

  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',
    ranksep: RANKSEP,
    nodesep: NODESEP,
    marginx: 24,
    marginy: 24,
  });
  for (const n of inNodes) {
    g.setNode(n.id, { width: NODE_W, height: NODE_H });
  }
  for (const e of inEdges) {
    if (e.source && e.target) g.setEdge(e.source, e.target);
  }
  dagre.layout(g);

  const nodes = inNodes.map((n) => {
    const d = g.node(n.id);
    return {
      id: n.id,
      type: 'manifestNode',
      data: { ...(n.data || {}) },
      position: d ? { x: d.x - NODE_W / 2, y: d.y - NODE_H / 2 } : { x: 0, y: 0 },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: { ...STATIC_NODE_STYLE, ...nodeStyleFor(n.data?.aslType) },
    };
  });

  const edges = inEdges.map((e, i) => ({
    id: e.id || `e-${e.source}-${e.target}-${i}`,
    source: e.source,
    target: e.target,
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#94a3b8', strokeWidth: 1.5 },
  }));

  return { nodes, edges };
}

/**
 * @param {{ data: {nodes:Array, edges:Array}, highlightLogicalId?: string }} props —
 *   the manifest graph (``manifestToReactFlow`` output). ``highlightLogicalId`` amber-rings
 *   the workbench-selected agent's node (Agents tab) AND gently frames it, so the user knows
 *   which node they're editing on a complex tree. ``fitView`` frames the whole tree on load.
 */
export default function ManifestTreeFlow({
  data, highlightLogicalId = null, statusByAgentId = null, onAgentClick = null,
  // Canvas background — defaults to the workbench's gradient; the room's docked tree passes a solid
  // white so the whole tree block reads as one clean white surface.
  canvasClassName = 'bg-gradient-to-br from-gray-50 to-blue-50',
  // Manual stage cap: agentIds whose stage-depth exceeds the cap → rendered DIMMED ("disabled this
  // release; part of the team, not engaged"). Set (or null). Workbench passes the live-draft cap;
  // the room passes the frozen-release cap. See ``cappedOutAgentIds``.
  dimmedAgentIds = null,
}) {
  // TOPOLOGY LAYOUT — dagre runs ONCE per topology (memoized on ``data`` only), NOT on every
  // status tick. This is the fix for the live-run graph inconsistency: previously dagre re-ran +
  // a fresh ``nodes`` array was handed to an uncontrolled ReactFlow on every status change, which
  // ReactFlow doesn't reliably reconcile (hence tints landing only after a hard refresh).
  const { nodes: baseNodes, edges } = useMemo(() => layoutTopology(data), [data]);

  // CONTROLLED nodes: ReactFlow owns the node store (positions, drags). We seed it from the
  // positioned topology and mutate style/className/highlight IN PLACE when status/selection change.
  const [nodes, setNodes, onNodesChange] = useNodesState(baseNodes);

  // Re-seed the store when the TOPOLOGY changes (new network / release), applying the current live
  // overlay so the first paint is already tinted.
  useEffect(() => {
    setNodes(baseNodes.map((n) => decorateNode(n, highlightLogicalId, statusByAgentId, dimmedAgentIds)));
    // Only on topology change — status/highlight-only updates are handled by the effect below so
    // node POSITIONS (incl. user drags) are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseNodes, setNodes]);

  // LIVE overlay: on every status/selection/cap tick, recompute each existing node's style/
  // className/highlight IN PLACE (keeping its current position). ReactFlow reconciles per-node, so
  // tints (and the capped-out dim, which updates live as the user clicks Stage pills) update
  // reliably without a relayout or a hard refresh.
  useEffect(() => {
    setNodes((prev) => prev.map((n) => decorateNode(n, highlightLogicalId, statusByAgentId, dimmedAgentIds)));
  }, [statusByAgentId, highlightLogicalId, dimmedAgentIds, setNodes]);

  const apiRef = useRef(null);
  // Click a node → tell the room which agent to load into the shared container (room docked tree).
  const handleNodeClick = useMemo(() => (
    typeof onAgentClick === 'function'
      ? (_e, node) => { const id = nodeAgentId(node); if (id) onAgentClick(id); }
      : undefined
  ), [onAgentClick]);

  // HYBRID pan/zoom + auto-follow. We keep the nice "frame the selected agent" animation, but ONLY
  // when the SELECTION changes — never on a status tick. Previously this effect depended on
  // ``nodes`` (which is re-set on EVERY live status update), so fitView re-fired constantly and
  // stomped whatever the user had just panned/zoomed to — they lost manual control. Now: gate on
  // ``highlightLogicalId`` alone (via a ref that remembers the last framed selection), and read the
  // current nodes from a ref so we don't depend on them. Between selection changes the viewport is
  // fully the user's — drag to pan, wheel/pinch to zoom, Controls to zoom/fit.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const lastFramedRef = useRef(null);
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !highlightLogicalId) return;
    // Only auto-frame when the SELECTION actually changed (not a re-render / status tick).
    if (lastFramedRef.current === highlightLogicalId) return;
    const match = (nodesRef.current || []).find((n) => matchesSelected(n, highlightLogicalId));
    if (!match?.id) return;
    lastFramedRef.current = highlightLogicalId;
    const id = match.id;
    requestAnimationFrame(() => {
      try {
        // Slow, smooth glide to the selected agent (800ms) — the deliberate "focus" feel the user
        // liked, not a fast snap. It fires ONCE per selection now (see the gate above), so this is
        // the true animation the user sees — no re-fire restarting it mid-flight.
        api.fitView({ nodes: [{ id }], duration: 800, padding: 0.6, minZoom: 0.12, maxZoom: 1.4 });
      } catch {
        /* noop */
      }
    });
  }, [highlightLogicalId]);

  if (!nodes.length) {
    return (
      <div className={`flex items-center justify-center h-full text-gray-500 text-sm ${canvasClassName}`}>
        No diagram available yet
      </div>
    );
  }

  return (
    <div className={`w-full h-full ${canvasClassName}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        nodeTypes={nodeTypes}
        onInit={(instance) => { apiRef.current = instance; }}
        onNodeClick={handleNodeClick}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable={!!handleNodeClick}
        proOptions={{ hideAttribution: true }}
        minZoom={0.1}
        maxZoom={1.5}
        // HYBRID control: full manual pan/zoom is always on (auto-follow only nudges on a NEW
        // selection — see the effect above). Explicit so the user can always drag to pan, scroll
        // to zoom, and pinch on trackpads — independent of the auto-frame.
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        panOnScroll={false}
        zoomOnDoubleClick
      >
        <Background gap={16} color="#e2e8f0" />
        {/* Controls include the fit-view + zoom buttons so the user can reset the framing anytime. */}
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
